// components/FeaturedExperience.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Box, Grid, Typography, Button, Container } from '@mui/material';
import { Link } from 'react-router-dom';

const FeaturedExperience = () => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [nextVideoIndex, setNextVideoIndex] = useState(1);
  const [activeVideo, setActiveVideo] = useState('current');
  const [transitioning, setTransitioning] = useState(false);
  const currentVideoRef = useRef(null);
  const nextVideoRef = useRef(null);
  
  const videos = [
    "https://videos.pexels.com/video-files/8263445/8263445-uhd_1440_2732_25fps.mp4",
    "https://videos.pexels.com/video-files/8262688/8262688-uhd_1440_2732_25fps.mp4",
    // Add more videos to this array as needed
  ];
  
  // Max duration to play each video (in seconds)
  const maxDuration = 15;
  
  // Initialize both videos
  useEffect(() => {
    if (currentVideoRef.current) {
      currentVideoRef.current.currentTime = 0;
      currentVideoRef.current.play().catch(e => console.log('Video play error:', e));
    }
    
    // Preload next video
    if (nextVideoRef.current) {
      nextVideoRef.current.load();
    }
  }, []);
  
  // Monitor current video for transition timing
  useEffect(() => {
    const videoElement = activeVideo === 'current' ? currentVideoRef.current : nextVideoRef.current;
    
    // Handler for time update to check if we should end early
    const handleTimeUpdate = () => {
      if (!transitioning && videoElement && videoElement.currentTime >= maxDuration) {
        startTransition();
      }
    };
    
    // Handler for when video ends naturally
    const handleEnded = () => {
      if (!transitioning) {
        startTransition();
      }
    };
    
    if (videoElement) {
      videoElement.addEventListener('timeupdate', handleTimeUpdate);
      videoElement.addEventListener('ended', handleEnded);
    }
    
    return () => {
      if (videoElement) {
        videoElement.removeEventListener('timeupdate', handleTimeUpdate);
        videoElement.removeEventListener('ended', handleEnded);
      }
    };
  }, [activeVideo, transitioning]);
  
  const startTransition = () => {
    if (transitioning) return;
    setTransitioning(true);
    
    // Get the inactive video ref and prepare it
    const inactiveVideoRef = activeVideo === 'current' ? nextVideoRef : currentVideoRef;
    const newVideoIndex = (activeVideo === 'current' ? nextVideoIndex : currentVideoIndex);
    const nextIndex = (newVideoIndex + 1) % videos.length;
    
    // Prepare the inactive video (which will become active)
    if (inactiveVideoRef.current) {
      inactiveVideoRef.current.src = videos[newVideoIndex];
      inactiveVideoRef.current.currentTime = 0;
      
      // Pre-play the video to ensure it's ready (muted)
      const playPromise = inactiveVideoRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Video started playing, now crossfade
            setTimeout(() => {
              setActiveVideo(activeVideo === 'current' ? 'next' : 'current');
              
              // Update indices for next transition
              if (activeVideo === 'current') {
                setCurrentVideoIndex(nextIndex);
              } else {
                setNextVideoIndex(nextIndex);
              }
              
              // Reset transition state after animation completes
              setTimeout(() => {
                setTransitioning(false);
              }, 600);
            }, 50);
          })
          .catch(e => {
            console.log('Video play error:', e);
            setTransitioning(false);
          });
      }
    }
  };
  
  return (
    <Box sx={{ 
      py: 12, 
      bgcolor: '#0a192f',
      backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(25, 55, 103, 0.4) 0%, rgba(10, 25, 47, 0.1) 100%)',
      boxShadow: 'inset 0 5px 10px rgba(0,0,0,0.2), inset 0 -5px 10px rgba(0,0,0,0.2)',
      borderTop: '1px solid rgba(255,255,255,0.05)',
      borderBottom: '1px solid rgba(255,255,255,0.05)',
    }}>
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ position: 'relative', height: '750px', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
              <Box
                component="video"
                ref={currentVideoRef}
                playsInline
                muted
                autoPlay
                src={videos[currentVideoIndex]}
                alt="Cinema experience"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center 30%',
                  opacity: activeVideo === 'current' ? 1 : 0,
                  transition: 'opacity 0.6s ease-in-out',
                  zIndex: activeVideo === 'current' ? 2 : 1,
                }}
              />
              <Box
                component="video"
                ref={nextVideoRef}
                playsInline
                muted
                autoPlay
                src={videos[nextVideoIndex]}
                alt="Cinema experience"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center 30%',
                  opacity: activeVideo === 'next' ? 1 : 0,
                  transition: 'opacity 0.6s ease-in-out',
                  zIndex: activeVideo === 'next' ? 2 : 1,
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ px: { xs: 1, md: 4 } }}>
              <Typography 
                variant="h2" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700, 
                  background: 'linear-gradient(45deg, #f5b400 30%, #f8d57e 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 2px 8px rgba(245,180,0,0.3)',
                  animation: 'slideInRight 0.8s ease-out',
                  '@keyframes slideInRight': {
                    '0%': { opacity: 0, transform: 'translateX(50px)' },
                    '100%': { opacity: 1, transform: 'translateX(0)' }
                  }
                }}
              >
                Experience Cinema Like Never Before
              </Typography>
              
              <Typography variant="body1" paragraph sx={{ 
                mb: 3, 
                fontSize: '1.1rem',
                animation: 'fadeIn 1s ease-out 0.2s both',
                '@keyframes fadeIn': {
                  '0%': { opacity: 0 },
                  '100%': { opacity: 1 }
                }
              }}>
                At Stellar Cinemas, we've reimagined the movie-going experience with state-of-the-art technology, premium comfort, and immersive environments that transport you into the world of film.
              </Typography>
              
              <Box sx={{ 
                mb: 4,
                animation: 'fadeIn 1s ease-out 0.4s both'
              }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Luxury Reclining Seats
                </Typography>
                <Typography variant="body1" paragraph>
                  Sink into our plush, leather recliners with extra legroom and adjustable headrests for the ultimate comfort during any movie.
                </Typography>
                
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Cutting-Edge Projection & Sound
                </Typography>
                <Typography variant="body1" paragraph>
                  Experience crystal-clear 4K laser projection and immersive Dolby Atmos sound that puts you in the center of the action.
                </Typography>
                
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Themed Screening Rooms
                </Typography>
                <Typography variant="body1" paragraph>
                  Choose from our uniquely designed theaters – from Classic Hollywood glamour to futuristic Sci-Fi environments to family-friendly spaces.
                </Typography>
              </Box>
              
              <Button 
                component={Link} 
                to="/experience" 
                variant="outlined" 
                color="secondary" 
                size="large"
                sx={{ 
                  borderWidth: 2,
                  '&:hover': { 
                    borderWidth: 2,
                    transform: 'scale(1.05)'
                  },
                  transition: 'transform 0.3s ease',
                  animation: 'fadeInUp 1s ease-out 0.6s both',
                  '@keyframes fadeInUp': {
                    '0%': { opacity: 0, transform: 'translateY(20px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' }
                  }
                }}
              >
                Explore Our Theaters
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default FeaturedExperience;